// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

import * as OperatorSchedulesActions from '../actions/operator-schedules.actions';
import { MatDialog } from '@angular/material/dialog';
import { MomentService, OperatorSchedulesApiService } from '@fc-core';
import { Action } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from '@fc-core/services/notification.service';
import { getErrorMessage } from '@fc-core/services/notification.utils';
import { inject } from '@angular/core';

@Injectable()
export class OperatorSchedulesEffects {
  private notifications = inject(NotificationService);

  constructor(
    private actions$: Actions,
    private operatorScheduleService: OperatorSchedulesApiService,
    private dialog: MatDialog,
    private momentService: MomentService,
  ) {}

  loadOperatorSchedules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OperatorSchedulesActions.loadOperatorSchedules),
      switchMap(({ options }) => {
        return this.operatorScheduleService.loadAll(options).pipe(
          map((events) => {
            return OperatorSchedulesActions.loadOperatorSchedulesSuccess({
              events: events,
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(OperatorSchedulesActions.operatorSchedulesError({ error })),
          ),
        );
      }),
    ),
  );

  loadOperatorAvailableVehicles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OperatorSchedulesActions.loadAllAvailableVehicles),
      switchMap(({ options }) => {
        return this.operatorScheduleService
          .loadAllAvailableVehicles(options)
          .pipe(
            map((vehicleIdList) => {
              return OperatorSchedulesActions.loadAllAvailableVehiclesSuccess({
                vehicleIdList,
              });
            }),
            catchError((error: HttpErrorResponse) =>
              of(OperatorSchedulesActions.operatorSchedulesError({ error })),
            ),
          );
      }),
    ),
  );

  loadAssignedVehicles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OperatorSchedulesActions.loadAllAssignedVehicles),
      switchMap(({ options }) => {
        return this.operatorScheduleService
          .loadAllAssignedVehicles(options)
          .pipe(
            map((vehicleIdList) => {
              return OperatorSchedulesActions.loadAllAssignedVehiclesSuccess({
                vehicleIdList,
              });
            }),
            catchError((error: HttpErrorResponse) =>
              of(OperatorSchedulesActions.operatorSchedulesError({ error })),
            ),
          );
      }),
    ),
  );

  addOperatorSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OperatorSchedulesActions.addOperatorSchedules),
      switchMap(({ event }) => {
        return this.operatorScheduleService.create(event).pipe(
          mergeMap((schedules) => {
            this.dialog.closeAll();
            this.notifications.showSuccess('Vehicle Successfully Added');
            return [
              OperatorSchedulesActions.addOperatorSchedulesSuccess({
                event: schedules,
              }),
              OperatorSchedulesActions.removeAvailableVehicle({
                vehicleId: event.vehicle,
              }),
              this.loadAssignedVehiclesAction(event.operator),
            ];
          }),
          catchError((error: HttpErrorResponse) => {
            const errorMessage = getErrorMessage(error);
            this.notifications.showError(errorMessage);
            return of(
              OperatorSchedulesActions.operatorSchedulesError({ error }),
            );
          }),
        );
      }),
    ),
  );

  updateOperatorSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OperatorSchedulesActions.updateOperatorSchedules),
      switchMap(({ event }) => {
        return this.operatorScheduleService.update(event).pipe(
          mergeMap(() => {
            this.dialog.closeAll();
            this.notifications.showSuccess('Vehicle Successfully Updated');
            return [
              OperatorSchedulesActions.updateOperatorSchedulesSuccess({
                event,
              }),
              this.loadAssignedVehiclesAction(event.operator),
            ];
          }),
          catchError((error: HttpErrorResponse) => {
            const errorMessage = getErrorMessage(error);
            this.notifications.showError(errorMessage);
            return of(
              OperatorSchedulesActions.operatorSchedulesError({ error }),
            );
          }),
        );
      }),
    ),
  );

  deleteOperatorSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OperatorSchedulesActions.deleteOperatorSchedules),
      switchMap(({ event }) => {
        return this.operatorScheduleService.delete(event).pipe(
          switchMap((id) => {
            this.dialog.closeAll();
            this.notifications.showSuccess('Vehicle Successfully Deleted');
            return [
              OperatorSchedulesActions.deleteOperatorSchedulesSuccess({
                id: id,
              }),
              this.loadAssignedVehiclesAction(event.operator),
            ];
          }),
          catchError((error: HttpErrorResponse) => {
            const errorMessage = getErrorMessage(error);
            this.notifications.showError(errorMessage);
            return of(
              OperatorSchedulesActions.operatorSchedulesError({ error }),
            );
          }),
        );
      }),
    ),
  );

  loadAssignedVehiclesAction(operatorId: number): Action {
    return OperatorSchedulesActions.loadAllAssignedVehicles({
      options: {
        currentId: operatorId,
        lower: this.momentService.moment().startOf('day').toISOString(),
        upper: this.momentService.moment().endOf('day').toISOString(),
      },
    });
  }
}
